import React from 'react';
import { Eye, EyeOff, Mountain, Zap, Tag, Maximize2, Grid3x3, Flame, Sun, Waves, TreePine, Layers } from 'lucide-react';
import { useUiStore } from '../stores/useUiStore';
import type { ColorMode } from '../types';

const FitToViewButton: React.FC = () => {
  const resetCamera = useUiStore((s) => s.resetCamera);
  return (
    <button
      onClick={resetCamera}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors min-h-touch text-slate-300 hover:bg-slate-800/70"
    >
      <Maximize2 className="h-4 w-4" />
      <span className="text-sm font-medium">Fit to View</span>
    </button>
  );
};

const ToggleGridButton: React.FC = () => {
  const showGrid = useUiStore((s) => s.showGrid);
  const toggleGrid = useUiStore((s) => s.toggleGrid);
  return (
    <button
      onClick={toggleGrid}
      aria-pressed={showGrid}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors min-h-touch text-slate-300 hover:bg-slate-800/70"
    >
      <Grid3x3 className="h-4 w-4" />
      <span className="text-sm font-medium">Toggle Grid</span>
      {showGrid && <div className="h-2 w-2 rounded-full bg-sky-400" />}
    </button>
  );
};

const LAYERS: { id: ColorMode; label: string; icon: React.ReactNode }[] = [
  { id: 'elevation', label: 'Elevation', icon: <Mountain className="h-4 w-4" /> },
  { id: 'intensity', label: 'Intensity', icon: <Zap className="h-4 w-4" /> },
  { id: 'classification', label: 'Classification', icon: <Tag className="h-4 w-4" /> },
  { id: 'bal', label: 'BAL Fire', icon: <Flame className="h-4 w-4" /> },
  { id: 'solar', label: 'Solar', icon: <Sun className="h-4 w-4" /> },
  { id: 'flow', label: 'Flow', icon: <Waves className="h-4 w-4" /> },
  { id: 'canopy', label: 'Canopy', icon: <TreePine className="h-4 w-4" /> },
  { id: 'substrate', label: 'Substrate', icon: <Layers className="h-4 w-4" /> },
];

export const LayerSwitcher: React.FC = () => {
  const activeLayer = useUiStore((s) => s.activeLayer);
  const setActiveLayer = useUiStore((s) => s.setActiveLayer);
  const showHud = useUiStore((s) => s.showHud);
  const toggleHud = useUiStore((s) => s.toggleHud);
  const showLayerPanel = useUiStore((s) => s.showLayerPanel);

  if (!showLayerPanel) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        Layers
      </h2>

      <div className="flex flex-col gap-1">
        {LAYERS.map((layer) => {
          const isActive = activeLayer === layer.id;
          return (
            <button
              key={layer.id}
              onClick={() => setActiveLayer(layer.id)}
              aria-pressed={isActive}
              className={[
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors min-h-touch',
                isActive
                  ? 'bg-sky-900/30 text-sky-300 border border-sky-800/50'
                  : 'text-slate-300 hover:bg-slate-800/70 border border-transparent',
              ].join(' ')}
            >
              {layer.icon}
              <span className="flex-1 text-sm font-medium">{layer.label}</span>
              {isActive && (
                <div className="h-2 w-2 rounded-full bg-sky-400" />
              )}
            </button>
          );
        })}
      </div>

      <div className="my-1 h-px bg-slate-700/50" />

      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        View
      </h2>

      <div className="flex flex-col gap-1">
        <button
          onClick={toggleHud}
          aria-pressed={showHud}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors min-h-touch text-slate-300 hover:bg-slate-800/70"
        >
          {showHud ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          <span className="text-sm font-medium">HUD Overlay</span>
        </button>

        <FitToViewButton />
        <ToggleGridButton />
      </div>
    </div>
  );
};
